import { reaction } from 'mobx';
import { useEffect, useRef, useState } from 'react';

import type { ReviewSaveStatus } from '../TranslationReviewHeader';
import type { TranslationStore } from '@src/stores-v2/TranslationStore';

/** How long "Saved" stays up before the status returns to its idle copy. */
export const SAVED_HOLD_MS = 2000;

/**
 * PLAT-11649 — the Translation Review's ambient save state for ONE language.
 *
 * Follows `TranslationStore.isSaving(language)`: `saving` while any persist is
 * in flight; when the last one lands, `saved` for `SAVED_HOLD_MS`, then `idle`
 * (whose copy is "Changes save automatically"). A persist that fails goes
 * straight back to `idle` — status is ambient, failure is an event — and the
 * failure reaches `onFailure` so the host can raise a toast. Both scoped to
 * this language: a save for another language is not this review's business.
 *
 * Driven by a MobX `reaction` rather than by reading the store in render, so it
 * behaves the same inside a non-`observer` host.
 */
export function useTranslationSaveStatus(
  translationStore: TranslationStore,
  language: string,
  options: { onFailure?: (error: unknown) => void } = {},
): ReviewSaveStatus {
  const [status, setStatus] = useState<ReviewSaveStatus>(() =>
    translationStore.isSaving(language) ? 'saving' : 'idle',
  );
  const holdTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Read through a ref so the subscription below never re-binds on a new
  // callback identity — the host passes an inline function.
  const onFailureRef = useRef(options.onFailure);
  onFailureRef.current = options.onFailure;

  useEffect(() => {
    // Whether the language's persists are in flight, and whether the latest
    // landing was a success (the timestamp moved) — one reaction, so the two
    // reads are consistent.
    const disposeReaction = reaction(
      () => ({
        saving: translationStore.isSaving(language),
        savedAt: translationStore.lastSavedAtFor(language),
      }),
      (next, prev) => {
        if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
        if (next.saving) {
          setStatus('saving');
          return;
        }
        if (prev.saving && next.savedAt !== prev.savedAt) {
          setStatus('saved');
          holdTimerRef.current = setTimeout(
            () => setStatus('idle'),
            SAVED_HOLD_MS,
          );
          return;
        }
        // Settled without a new landing: the last persist failed.
        setStatus('idle');
      },
    );
    const unsubscribeFailed = translationStore.onSaveFailed((lang, error) => {
      if (lang !== language) return;
      onFailureRef.current?.(error);
    });
    return () => {
      disposeReaction();
      unsubscribeFailed();
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    };
  }, [translationStore, language]);

  return status;
}
